import { useCallback, useEffect, useState } from 'react'
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion'

type TypingPreferenceValue = 'on' | 'off'

const STORAGE_KEY = 'pit_typing_animation'

const readStoredPreference = (): TypingPreferenceValue => {
  if (typeof window === 'undefined') {
    return 'on'
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored === 'off' ? 'off' : 'on'
  } catch {
    return 'on'
  }
}

const useTypingAnimationPreference = () => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [preference, setPreference] = useState<TypingPreferenceValue>(() => readStoredPreference())

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY || event.storageArea !== window.localStorage) {
        return
      }
      if (event.newValue === 'off' || event.newValue === 'on') {
        setPreference(event.newValue)
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const setTypingAnimationEnabled = useCallback((nextEnabled: boolean) => {
    const nextValue: TypingPreferenceValue = nextEnabled ? 'on' : 'off'
    setPreference(nextValue)

    if (typeof window === 'undefined') {
      return
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, nextValue)
    } catch {
      // no-op
    }
  }, [])

  const typingAnimationEnabled = !prefersReducedMotion && preference !== 'off'

  return {
    typingAnimationEnabled,
    typingAnimationPreference: preference,
    typingAnimationBlocked: prefersReducedMotion,
    setTypingAnimationEnabled,
  }
}

export default useTypingAnimationPreference
